import { supabase } from "../config/supabase";

export interface DadosEmpresa {
  cnpj: string;
  razao_social: string;
  nome_fantasia?: string;
  cnae_principal: {
    codigo: string;
    descricao: string;
  };
  endereco: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    municipio: string;
    uf: string;
    cep: string;
  };
  situacao: string;
  porte: string;
}

export interface EmpresaClassificada {
  cnpj: string;
  razao_social: string;
  nome_fantasia?: string;
  cnae_principal: string;
  cnae_descricao: string;
  tipo_comissao: "CIPA" | "CIPATR" | "CIPAMIN";
  grau_risco: number;
  endereco_completo: string;
  municipio: string;
  uf: string;
  cep: string;
  situacao: string;
  porte: string;
}

/**
 * Consulta dados da empresa via CNPJ
 */
export const consultarCNPJ = async (
  cnpj: string,
): Promise<DadosEmpresa | null> => {
  try {
    const cnpjLimpo = cnpj.replace(/\D/g, "");

    if (cnpjLimpo.length !== 14) {
      throw new Error("CNPJ deve ter 14 dígitos");
    }

    // Tenta BrasilAPI primeiro
    try {
      const response = await fetch(
        `https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`,
      );

      if (response.ok) {
        const data = await response.json();
        return {
          cnpj: data.cnpj,
          razao_social: data.razao_social,
          nome_fantasia: data.nome_fantasia,
          cnae_principal: {
            codigo: data.cnae_fiscal,
            descricao: data.cnae_fiscal_descricao,
          },
          endereco: {
            logradouro: data.logradouro,
            numero: data.numero,
            complemento: data.complemento,
            bairro: data.bairro,
            municipio: data.municipio,
            uf: data.uf,
            cep: data.cep,
          },
          situacao: data.descricao_situacao_cadastral,
          porte: data.porte,
        };
      }
    } catch (error) {
      console.warn("BrasilAPI falhou, tentando ReceitaWS...");
    }

    // Fallback para ReceitaWS
    try {
      const response = await fetch(
        `https://www.receitaws.com.br/v1/cnpj/${cnpjLimpo}`,
      );

      if (response.ok) {
        const data = await response.json();

        if (data.status === "ERROR") {
          throw new Error(data.message || "CNPJ não encontrado");
        }

        return {
          cnpj: data.cnpj,
          razao_social: data.nome,
          nome_fantasia: data.fantasia,
          cnae_principal: {
            codigo: data.atividade_principal[0]?.code || "",
            descricao: data.atividade_principal[0]?.text || "",
          },
          endereco: {
            logradouro: data.logradouro,
            numero: data.numero,
            complemento: data.complemento,
            bairro: data.bairro,
            municipio: data.municipio,
            uf: data.uf,
            cep: data.cep,
          },
          situacao: data.situacao,
          porte: data.porte,
        };
      }
    } catch (error) {
      console.warn("ReceitaWS também falhou:", error);
    }

    return null;
  } catch (error) {
    console.error("Erro ao consultar CNPJ:", error);
    return null;
  }
};

/**
 * Obtém o tipo de comissão baseado no CNAE
 */
export const obterTipoComissaoPorCNAE = async (
  codigoCNAE: string,
): Promise<"CIPA" | "CIPAMIN" | "CIPATR"> => {
  try {
    const cnaeLimpo = codigoCNAE.replace(/\D/g, "");
    const cnaeParaBusca = cnaeLimpo.substring(0, 5);

    // Busca na tabela cp_cnae
    let { data, error } = await supabase
      .from("cp_cnae")
      .select("tipo_comissao")
      .eq("cnae", cnaeParaBusca)
      .maybeSingle();

    if (error) {
      console.error(
        `Erro ao buscar tipo comissão para CNAE ${cnaeParaBusca}:`,
        error,
      );
      return "CIPA";
    }

    // Se não encontrou, tenta busca com LIKE nos 4 primeiros dígitos
    if (!data && cnaeParaBusca.length >= 4) {
      const cnae4Digitos = cnaeParaBusca.substring(0, 4);

      const { data: dataLike, error: errorLike } = await supabase
        .from("cp_cnae")
        .select("tipo_comissao")
        .like("cnae", `%${cnae4Digitos}%`)
        .limit(1)
        .single();

      if (!errorLike && dataLike) {
        return dataLike.tipo_comissao as "CIPA" | "CIPAMIN" | "CIPATR";
      }
    }

    if (!data) {
      console.warn(
        `CNAE ${cnaeParaBusca} não encontrado, usando CIPA como padrão`,
      );
      return "CIPA";
    }

    return data.tipo_comissao as "CIPA" | "CIPAMIN" | "CIPATR";
  } catch (error) {
    console.error("Erro ao buscar tipo comissão por CNAE:", error);
    return "CIPA";
  }
};

/**
 * Obtém o grau de risco baseado no CNAE
 */
export const obterGrauRiscoPorCNAE = async (
  codigoCNAE: string,
): Promise<number> => {
  try {
    const cnaeLimpo = codigoCNAE.replace(/\D/g, "");
    const cnaeParaBusca = cnaeLimpo.substring(0, 5);

    const { data, error } = await supabase
      .from("cp_cnae")
      .select("grau_risco")
      .eq("cnae", cnaeParaBusca)
      .maybeSingle();

    if (error || !data) {
      console.warn(
        `Grau de risco não encontrado para CNAE ${cnaeParaBusca}, usando 1 como padrão`,
      );
      return 1;
    }

    return data.grau_risco;
  } catch (error) {
    console.error("Erro ao buscar grau de risco por CNAE:", error);
    return 1;
  }
};

/**
 * Classifica automaticamente uma empresa baseada no CNPJ
 */
export const classificarEmpresaPorCNPJ = async (
  cnpj: string,
): Promise<EmpresaClassificada | null> => {
  try {
    console.log(`🔍 Classificando empresa CNPJ: ${cnpj}`);

    // 1. Consultar dados da empresa
    const dadosEmpresa = await consultarCNPJ(cnpj);
    if (!dadosEmpresa) {
      throw new Error("Não foi possível consultar os dados da empresa");
    }

    console.log(`✅ Dados obtidos: ${dadosEmpresa.razao_social}`);

    // 2. Obter tipo de comissão
    const tipoComissao = await obterTipoComissaoPorCNAE(
      dadosEmpresa.cnae_principal.codigo,
    );
    console.log(`📋 Tipo de comissão: ${tipoComissao}`);

    // 3. Obter grau de risco
    const grauRisco = await obterGrauRiscoPorCNAE(
      dadosEmpresa.cnae_principal.codigo,
    );
    console.log(`⚠️ Grau de risco: ${grauRisco}`);

    // 4. Formatar endereço completo
    const endereco = dadosEmpresa.endereco;
    const enderecoCompleto = [
      endereco.logradouro,
      endereco.numero,
      endereco.complemento ? `, ${endereco.complemento}` : "",
      ` - ${endereco.bairro}`,
      `, ${endereco.municipio} - ${endereco.uf}`,
      `, CEP: ${endereco.cep}`,
    ].join("");

    // 5. Retornar empresa classificada
    const empresaClassificada: EmpresaClassificada = {
      cnpj: dadosEmpresa.cnpj,
      razao_social: dadosEmpresa.razao_social,
      nome_fantasia: dadosEmpresa.nome_fantasia,
      cnae_principal: dadosEmpresa.cnae_principal.codigo,
      cnae_descricao: dadosEmpresa.cnae_principal.descricao,
      tipo_comissao: tipoComissao,
      grau_risco: grauRisco,
      endereco_completo: enderecoCompleto,
      municipio: endereco.municipio,
      uf: endereco.uf,
      cep: endereco.cep,
      situacao: dadosEmpresa.situacao,
      porte: dadosEmpresa.porte,
    };

    console.log(
      `🎯 Classificação completa: ${tipoComissao} (Risco ${grauRisco})`,
    );
    return empresaClassificada;
  } catch (error) {
    console.error("❌ Erro na classificação automática:", error);
    return null;
  }
};

/**
 * Formata endereço para exibição
 */
export const formatarEndereco = (
  endereco: DadosEmpresa["endereco"],
): string => {
  const partes = [
    endereco.logradouro,
    endereco.numero,
    endereco.complemento,
    endereco.bairro,
    `${endereco.municipio}/${endereco.uf}`,
    endereco.cep,
  ].filter(Boolean);

  return partes.join(", ");
};
